"use client";

import config from "@/app.config";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import { useEffect, useState } from "react";
import { ReviewType } from "@/types/review.type";
import axios from "axios";
import { format } from "date-fns";
import { Button, Card, CardContent, Rating, Skeleton } from "@mui/material";
import Link from "next/link";

export default function Reviews() {
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<ReviewType[]>([]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        const response = await axios.get(
          `${config.app.apiBaseUrl}/api/v2/reviews`,
        );

        setReviews(response.data.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <section className="py-4 max-w-8xl mx-auto px-4">
        <h4 className="text-center font-semibold text-xl md:text-4xl mb-2 md:mb-4">
          Customer Reviews
        </h4>

        <Swiper spaceBetween={12} slidesPerView="auto" loop>
          {[1, 2, 3, 4, 5].map((_, index) => (
            <SwiperSlide key={index} className="md:max-w-1/4 pb-4">
              <div className="block shadow-sm hover:shadow-lg duration-500">
                <Card>
                  <CardContent>
                    <div className="flex gap-2 flex-nowrap items-center">
                      <Skeleton
                        width={42}
                        height={42}
                        variant="circular"
                        className="shrink-0"
                      />
                      <div className="space-y-2">
                        <Skeleton variant="rounded" width={200} />
                        <Skeleton variant="rounded" width={100} />
                      </div>
                    </div>
                    <div className="mt-3">
                      <Rating size="small" value={0} readOnly />
                    </div>
                    <p className="text-sm space-y-2">
                      <Skeleton variant="rounded" />
                      <Skeleton variant="rounded" />
                      <Skeleton variant="rounded" />
                    </p>
                    <div className="mt-2">
                      <Skeleton variant="rounded" width={100} height={36} />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </section>
    );
  }

  if (reviews.length == 0) return null;

  return (
    <section className="py-4 max-w-8xl mx-auto px-4">
      <h4 className="text-center font-semibold text-xl md:text-4xl mb-2 md:mb-4">
        Customer Reviews
      </h4>

      <Swiper
        modules={[Autoplay]}
        autoplay={{ delay: 2500, disableOnInteraction: false }}
        spaceBetween={12}
        slidesPerView="auto"
        loop
      >
        {reviews.map((review, index) => (
          <SwiperSlide key={index} className="md:max-w-1/4 pb-4">
            <Link
              href={`${review.url}`}
              rel="noopener noreferrer"
              target="_blank"
              className="block shadow-sm hover:shadow-lg duration-500"
            >
              <Card>
                <CardContent>
                  <div className="flex gap-2 flex-nowrap items-center">
                    {review.customer_image ? (
                      <Image
                        src={review.customer_image}
                        alt={review.customer_name}
                        width={42}
                        height={42}
                        className="size-[42px] rounded-full shrink-0"
                      />
                    ) : (
                      <div className="size-[42px] rounded-full shrink-0"></div>
                    )}
                    <div>
                      <div className="font-semibold">
                        {review.customer_name}
                      </div>
                      <div className="text-sm">
                        {format(review.date, "dd MMM, yyyy")}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <Rating
                      size="small"
                      value={Number(review.rating)}
                      readOnly
                    />
                  </div>
                  <p className="text-sm">
                    {review.comment.length > 120
                      ? review.comment.substring(0, 120) + "..."
                      : review.comment}
                  </p>
                </CardContent>
              </Card>
            </Link>
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  );
}
